import React, { useState, useMemo } from "react";
import { Calculator, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";

const usd = (n) => "$" + (Math.round(n)).toLocaleString();
const pct = (n) => (n * 100).toFixed(1) + "%";

const FIELDS = [
  { k: "players", label: "Monthly active players", step: 1000 },
  { k: "payingPct", label: "% who pay (IAP)", step: 1, suffix: "%" },
  { k: "arppu", label: "Avg $ per paying user / mo", step: 1, prefix: "$" },
  { k: "adRevenue", label: "Ad revenue / mo", step: 500, prefix: "$" },
  { k: "sponsorRevenue", label: "Sponsor revenue / mo", step: 500, prefix: "$" },
  { k: "paymentFeePct", label: "Payment fees", step: 0.5, suffix: "%" },
  { k: "hosting", label: "Hosting + ops / mo", step: 100, prefix: "$" },
  { k: "poolPct", label: "Rewards pool (% of net)", step: 1, suffix: "%" },
  { k: "hardcorePct", label: "% players who qualify", step: 1, suffix: "%" },
  { k: "avgRewardCost", label: "Avg reward cost / qualified", step: 0.25, prefix: "$" },
];

const DEFAULTS = {
  players: 10000, payingPct: 10, arppu: 5, adRevenue: 3000, sponsorRevenue: 2000,
  paymentFeePct: 1.5, hosting: 800, poolPct: 7, hardcorePct: 10, avgRewardCost: 1,
};

export default function AdminEconomics() {
  const [v, setV] = useState(DEFAULTS);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val === "" ? 0 : Number(val) }));

  const m = useMemo(() => {
    const iap = v.players * (v.payingPct / 100) * v.arppu;
    const gross = iap + v.adRevenue + v.sponsorRevenue;
    const fees = gross * (v.paymentFeePct / 100);
    const net = gross - fees - v.hosting;
    const pool = Math.max(0, net * (v.poolPct / 100));
    const liability = v.players * (v.hardcorePct / 100) * v.avgRewardCost;
    const actualPayout = Math.min(pool, liability);
    const profit = net - actualPayout;
    const covered = pool >= liability;
    return { iap, gross, fees, net, pool, liability, actualPayout, profit, covered };
  }, [v]);

  const Row = ({ label, val, strong, c }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-200">
      <span className={`text-sm ${strong ? "font-semibold text-slate-900" : "text-slate-500"}`}>{label}</span>
      <span className={`font-display ${strong ? "text-lg" : "text-sm"} ${c || "text-slate-900"}`}>{val}</span>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-6 max-w-2xl mx-auto" data-testid="admin-economics-page">
      <div className="flex items-center gap-2"><Calculator className="text-[#00E5FF]" size={24} /><h1 className="font-display text-2xl">Rewards Economics Model</h1></div>
      <p className="text-sm text-slate-500 mt-1">Edit any input to see if the rewards pool stays profitable. Money flows in only; cash rewards are capped by the pool.</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <label key={f.k} className="block">
            <span className="label-xs text-slate-500">{f.label}</span>
            <div className="mt-1 flex items-center bg-white border border-slate-200 rounded-lg px-3 py-2">
              {f.prefix && <span className="text-slate-500 text-sm mr-1">{f.prefix}</span>}
              <input data-testid={`econ-${f.k}`} type="number" step={f.step} value={v[f.k]} onChange={(e) => set(f.k, e.target.value)}
                className="w-full bg-transparent text-sm text-slate-900 outline-none" />
              {f.suffix && <span className="text-slate-500 text-sm ml-1">{f.suffix}</span>}
            </div>
          </label>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-2"><TrendingUp size={16} className="text-[#16A34A]" /><h2 className="font-display text-lg">Monthly results</h2></div>
        <Row label="In-game purchases (IAP)" val={usd(m.iap)} />
        <Row label="+ Ads" val={usd(v.adRevenue)} />
        <Row label="+ Sponsors" val={usd(v.sponsorRevenue)} />
        <Row label="Gross revenue" val={usd(m.gross)} strong />
        <Row label="− Payment fees" val={"-" + usd(m.fees)} c="text-slate-500" />
        <Row label="− Hosting + ops" val={"-" + usd(v.hosting)} c="text-slate-500" />
        <Row label="Net revenue" val={usd(m.net)} strong c="text-[#16A34A]" />
        <Row label={`Rewards pool (${v.poolPct}% of net)`} val={usd(m.pool)} c="text-[#FFD700]" />
        <Row label="Reward liability (if all qualify)" val={usd(m.liability)} c="text-[#FFB300]" />
        <Row label="Actual payout (capped by pool)" val={usd(m.actualPayout)} c="text-[#FFD700]" />
        <Row label="Net profit after rewards" val={usd(m.profit)} strong c={m.profit >= 0 ? "text-[#16A34A]" : "text-[#FF0055]"} />
      </div>

      <div data-testid="econ-verdict" className={`mt-4 rounded-xl p-4 flex items-start gap-3 ${m.covered && m.profit > 0 ? "bg-[#16A34A]/10 border border-[#16A34A]/30" : "bg-[#FFB300]/10 border border-[#FFB300]/30"}`}>
        {m.covered && m.profit > 0 ? <CheckCircle2 className="text-[#16A34A] shrink-0" /> : <AlertTriangle className="text-[#FFB300] shrink-0" />}
        <div className="text-sm">
          {m.covered
            ? `The ${pct(v.poolPct / 100)} pool (${usd(m.pool)}) fully covers the ${usd(m.liability)} reward liability.`
            : `⚠️ The pool (${usd(m.pool)}) is smaller than the ${usd(m.liability)} liability — payouts are capped, so raise the pool %, lower avg reward cost, or grow revenue.`}
          {" "}Net profit after rewards: <span className="font-semibold">{usd(m.profit)}</span>/mo.
        </div>
      </div>
      <p className="mt-3 label-xs text-slate-400">Peg: ~250 Hustler Points ≈ $1 reward value. Rule of thumb: keep the pool at 5–10% of net so rewards scale with the business, never becoming an open-ended liability.</p>
    </div>
  );
}
